import {
  WalletBilling,
  WalletEntry,
  WalletPrices,
  WalletProductPrice,
  WalletProductService,
  findProductPrice,
  walletPriceSourceLabel,
  walletProductUnitSuffix,
  walletServiceLabel,
} from 'src/app/models/Wallet';

/**
 * "Quanto rende" do modal de recarga (CU-86ak77za5, pedido do Bruno/Misael de 10/10/2026).
 *
 * Funções PURAS, sem Angular e sem HTTP: é a parte de dinheiro do modal, e mora fora do
 * componente para ser testada sozinha. O componente só junta as respostas e chama daqui;
 * o template só desenha.
 *
 * ## O que NÃO se calcula aqui
 *
 * Preço. O preço de cada produto vem pronto de `GET /wallet/prices`, que pergunta à
 * carteira pelo MESMO motor do débito (contrato → assinante → padrão). Aqui ele só é
 * dividido. Nenhum markup, câmbio ou tabela de lista entra neste arquivo.
 *
 * ## Por que a quantidade é "≈"
 *
 * `unit_price_micros` é o preço de 1 unidade arredondado ao micro (HALF_UP), e a carteira
 * cobra `preço exato × N` arredondado UMA vez no total. Com preço que tem mais de 6 casas
 * (câmbio, contrato fracionado) a divisão pode errar por uma fração ínfima — para R$ 0,01
 * por unidade, no pior caso 0,005% da quantidade. É estimativa para o cliente decidir quanto
 * recarregar; nada é reservado ou cobrado a partir deste número.
 */

/** Ordem das linhas da tabela. RCS não existe no sistema — não há linha a mostrar. */
export const RECHARGE_YIELD_SERVICES: WalletProductService[] = ['sms', 'whatsapp', 'call'];

/** A ligação é cobrada em blocos de 30 s (`ceil(segundos / 30)` por chamada). */
export const CALL_BLOCK_SECONDS = 30;

/**
 * Situação de cada produto na tabela.
 *
 * - `priced`: a carteira cobra este canal e há preço → mostra preço e quantidade.
 * - `blocked`: a carteira cobra, mas não há preço resolvido para a conta → envio não sai;
 *   NUNCA mostrar `R$ 0,00` nem quantidade.
 * - `credits`: o canal desta conta sai do pacote de créditos, não deste saldo. O preço da
 *   carteira NÃO é o que o cliente paga nele, então não se mostra preço nem quantidade.
 */
export type RechargeYieldStatus = 'priced' | 'blocked' | 'credits';

export interface RechargeYieldRow {
  service: WalletProductService;
  /** "SMS", "WhatsApp", "Ligação". */
  label: string;
  status: RechargeYieldStatus;
  /** Preço DO CLIENTE por unidade; `null` fora de `priced`. */
  unitPriceMicros: number | null;
  /** "/ SMS enviado", "/ mensagem de WhatsApp", "/ bloco de 30 s de ligação". */
  unitSuffix: string;
  /** "Preço de contrato" / "Preço de assinante" / "Preço padrão"; `null` fora de `priced`. */
  sourceLabel: string | null;
  /**
   * Unidades inteiras que o saldo paga. `null` quando não se sabe (saldo não carregou)
   * ou quando a linha não é `priced`. Zero é zero de verdade (saldo zerado).
   */
  quantity: number | null;
  /** "SMS", "mensagens", "blocos de 30 s". */
  quantityUnitLabel: string;
  /**
   * Só na ligação: minutos de conversa que os blocos pagam, no MELHOR caso (cada chamada
   * arredonda para cima até o bloco seguinte, então é "até"). `null` nos outros produtos.
   */
  callMinutesUpTo: number | null;
}

const QUANTITY_UNIT_LABELS: { [service: string]: string } = {
  sms: 'SMS',
  whatsapp: 'mensagens',
  call: 'blocos de 30 s',
};

/**
 * O canal sai deste saldo? Sem `billing` (a leitura falhou) vale o que a tela da carteira
 * já faz em "Sua tarifa" (`MyWalletComponent.walletRateProducts`): mostra como carteira.
 */
export function isBilledByWallet(billing: WalletBilling | null, service: string): boolean {
  if (!billing) {
    return true;
  }

  const source = (billing as any)[service];

  return source === undefined || source === 'wallet';
}

/**
 * Unidades inteiras que `balanceMicros` paga a `unitPriceMicros` cada.
 *
 * `null` quando não dá para responder (saldo desconhecido, preço ausente/zero/negativo):
 * preço zero seria "de graça", que ninguém contratou, e dividiria por zero. Saldo negativo
 * (overdraft) rende zero, não quantidade negativa.
 */
export function affordableUnits(balanceMicros: number | null, unitPriceMicros: number | null): number | null {
  if (balanceMicros === null || balanceMicros === undefined || !isFinite(balanceMicros)) {
    return null;
  }

  if (unitPriceMicros === null || unitPriceMicros === undefined || !isFinite(unitPriceMicros) || unitPriceMicros <= 0) {
    return null;
  }

  // Inteiros em micros: a divisão é exata até 2^53, muito acima de qualquer saldo real.
  return Math.floor(Math.max(balanceMicros, 0) / unitPriceMicros);
}

/** Minutos inteiros que `blocks` blocos de 30 s somam (2 blocos = 1 min). */
export function callMinutesUpTo(blocks: number | null): number | null {
  if (blocks === null || blocks === undefined) {
    return null;
  }

  return Math.floor((blocks * CALL_BLOCK_SECONDS) / 60);
}

/**
 * Uma linha por produto cobrado em unidade (SMS, WhatsApp, ligação).
 *
 * @param prices        resposta de `GET /wallet/prices`; `null` (falhou) → nenhuma linha.
 * @param billing       resposta de `GET /wallet/billing`; `null` → todos como carteira.
 * @param balanceMicros saldo que vai render: disponível de hoje + recarga + bônus cotado
 *                      (o componente já soma em `projectedBalanceMicros`). `null` quando o
 *                      saldo de hoje não carregou — aí preço aparece, quantidade não.
 */
export function rechargeYieldRows(
  prices: WalletPrices | null,
  billing: WalletBilling | null,
  balanceMicros: number | null
): RechargeYieldRow[] {
  if (!prices || !prices.products) {
    return [];
  }

  const rows: RechargeYieldRow[] = [];

  RECHARGE_YIELD_SERVICES.forEach((service) => {
    const product: WalletProductPrice | null = findProductPrice(prices, service);

    // Produto que a carteira não devolveu não vira linha "bloqueada": não se sabe nada dele.
    if (!product) {
      return;
    }

    const base = {
      service,
      label: walletServiceLabel(service),
      unitSuffix: walletProductUnitSuffix(service),
      quantityUnitLabel: QUANTITY_UNIT_LABELS[service],
    };

    if (!isBilledByWallet(billing, service)) {
      rows.push({
        ...base,
        status: 'credits',
        unitPriceMicros: null,
        sourceLabel: null,
        quantity: null,
        callMinutesUpTo: null,
      });
      return;
    }

    const price = product.unit_price_micros;

    if (product.blocked || price === null || price === undefined || price <= 0) {
      rows.push({
        ...base,
        status: 'blocked',
        unitPriceMicros: null,
        sourceLabel: null,
        quantity: null,
        callMinutesUpTo: null,
      });
      return;
    }

    const quantity = affordableUnits(balanceMicros, price);

    rows.push({
      ...base,
      status: 'priced',
      unitPriceMicros: price,
      sourceLabel: walletPriceSourceLabel(product.source),
      quantity,
      callMinutesUpTo: service === 'call' ? callMinutesUpTo(quantity) : null,
    });
  });

  return rows;
}

// =============================================================================
// IA: faixa de custo por resposta, a partir do que a carteira JÁ cobrou
// =============================================================================

/**
 * Janela do histórico de IA. 30 dias acompanha preço e modelo recentes sem depender de
 * um mês inteiro de uso.
 */
export const AI_COST_WINDOW_DAYS = 30;

/** Uma página do extrato (teto do BFF): as respostas mais recentes da janela. */
export const AI_COST_SAMPLE_LIMIT = 200;

/**
 * Mínimo de cobranças para mostrar faixa. Abaixo disso o "entre X e Y" seria o acaso de
 * duas ou três conversas, e a tela fica só com o texto ("varia com o modelo").
 */
export const AI_COST_MIN_SAMPLE = 20;

/**
 * Percentis da faixa. Os 10% de cada ponta ficam de fora: uma melhoria de prompt longa
 * ou um teste de agente com histórico enorme puxariam o teto para um valor que quase
 * nenhuma resposta custa. A tela diz "em geral", não "no máximo".
 */
export const AI_COST_LOW_PERCENTILE = 10;
export const AI_COST_HIGH_PERCENTILE = 90;

export interface AiCostRange {
  /** Cobranças de IA que entraram na conta. */
  sampleSize: number;
  /** Percentil baixo do custo de UMA resposta, em micros (positivo). */
  lowMicros: number;
  /** Percentil alto do custo de UMA resposta, em micros (positivo). */
  highMicros: number;
  windowDays: number;
}

/**
 * Percentil pelo método do posto mais próximo (nearest-rank): sempre devolve um valor
 * que EXISTIU na amostra — nunca uma interpolação que nenhuma resposta custou.
 * `sorted` em ordem crescente e não vazio.
 */
export function nearestRankPercentile(sorted: number[], percentile: number): number {
  const rank = Math.ceil((percentile / 100) * sorted.length);
  const index = Math.min(Math.max(rank, 1), sorted.length) - 1;

  return sorted[index];
}

/**
 * Faixa de custo por resposta de IA a partir dos lançamentos REAIS da carteira.
 *
 * Por que daqui, e não de tokens × tabela de preço do modelo: cada resposta da IA vira
 * UM débito na carteira (`BillingService::chargeUsage` → `enqueueDebit`), já no preço que
 * o cliente paga (custo do modelo × câmbio × markup dele). Ler o débito é ler o que foi
 * cobrado; recalcular a partir de tokens seria refazer o motor de preço no painel e
 * inventar um "tamanho médio de mensagem" que não existe.
 *
 * O que entra: lançamento de `service: 'ai'` com valor de saída (negativo). Estorno,
 * crédito e outros serviços ficam de fora. Cada débito é uma chamada ao modelo — resposta
 * do agente ao lead, teste do agente no painel ou melhoria de prompt; os três são "uma
 * resposta da IA" e a tela fala assim.
 *
 * `null` quando não há amostra suficiente (`AI_COST_MIN_SAMPLE`): sem dado, sem número.
 */
export function aiCostRangeFromEntries(entries: WalletEntry[] | null): AiCostRange | null {
  const costs = (entries || [])
    .filter((entry) => !!entry
      && entry.service === 'ai'
      && typeof entry.amount_micros === 'number'
      && isFinite(entry.amount_micros)
      && entry.amount_micros < 0)
    .map((entry) => -entry.amount_micros)
    .sort((a, b) => a - b);

  if (costs.length < AI_COST_MIN_SAMPLE) {
    return null;
  }

  return {
    sampleSize: costs.length,
    lowMicros: nearestRankPercentile(costs, AI_COST_LOW_PERCENTILE),
    highMicros: nearestRankPercentile(costs, AI_COST_HIGH_PERCENTILE),
    windowDays: AI_COST_WINDOW_DAYS,
  };
}

/** Início da janela do histórico de IA, como instante ISO (o BFF aceita `date`). */
export function aiCostWindowStartIso(now: Date = new Date()): string {
  return new Date(now.getTime() - AI_COST_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
}
